// ============================================================================
// ecgPatients.ts — ПАЦИЕНТ в экране врача ЭКГ (чистые функции, без UI).
//
// Модель данных ЭКГ-модуля (hydra.medic.ecg): исследование — персональная
// запись справочника cardio_studies, у неё есть реквизит-владелец
// (owner_user_id, механизм OwnScope). Отдельного реквизита «пациент» в
// поставке 1.0.2 нет, поэтому пациентом считается ВЛАДЕЛЕЦ исследования.
// Если в базе появится явный реквизит пациента (patient_id и т.п. — его
// находит lib/ecgStudies по метаданным), он ПРИОРИТЕТНЕЕ владельца: запись
// будет связана с картотекой пациентов, а не с учётной записью.
//
// Здесь только группировка/сводка уже загруженных исследований — загрузка и
// запись остаются в lib/ecgStudies (SSoT RPC).
// ============================================================================

import { formatValue } from '@/lib/format';
import type { EcgStudyListItem } from '@/lib/ecgStudies';

/** Откуда взят пациент: отдельный реквизит записи или владелец исследования. */
export type EcgPatientSource = 'patient' | 'owner';

export interface EcgPatient {
    /** id записи пациента (реквизит patient…) либо id владельца исследования. */
    id: string;
    /** Имя из серверного представления (`*_presentation`); пусто — имя не указано. */
    name: string;
    source: EcgPatientSource;
}

/** patientOfStudy — пациент исследования (SSoT выбора источника). */
export function patientOfStudy(item: EcgStudyListItem): EcgPatient | null {
    if (item.patientId) {
        return { id: item.patientId, name: item.patientName, source: 'patient' };
    }
    if (item.ownerId) {
        return { id: item.ownerId, name: item.ownerName, source: 'owner' };
    }
    return null;
}

/** Ключ пациента для фильтров/группировок (id уникален только внутри источника). */
export function patientKey(patient: EcgPatient | null): string {
    return patient ? `${patient.source}:${patient.id}` : '';
}

export function studyPatientKey(item: EcgStudyListItem): string {
    return patientKey(patientOfStudy(item));
}

/**
 * patientName — имя для интерфейса. UUID никогда не показывается: если
 * сервер не дал представление, честно сообщаем, что имя не указано.
 */
export function patientDisplayName(patient: EcgPatient | null): string {
    if (!patient) return 'не указан';
    if (patient.name) return patient.name;
    return patient.source === 'patient'
        ? 'Пациент (имя не указано)'
        : 'Владелец исследования (имя не указано)';
}

export interface EcgPatientSummary {
    patient: EcgPatient;
    studies: EcgStudyListItem[];
    total: number;
    /** Разборы с заполненным заключением врача. */
    withConclusion: number;
    /** Разборы, ожидающие заключения. */
    awaitingConclusion: number;
    /** Последнее исследование (ISO из записи). */
    lastAt: string;
    /** Число различных статусов — для подписи «в работе/переснять». */
    statusCounts: Record<string, number>;
}

function compareStudies(a: EcgStudyListItem, b: EcgStudyListItem): number {
    const aKey = formatValue(a.createdAt, 'date:yyyy-MM-dd HH:mm:ss', 'timestamp') || a.createdAt;
    const bKey = formatValue(b.createdAt, 'date:yyyy-MM-dd HH:mm:ss', 'timestamp') || b.createdAt;
    return aKey.localeCompare(bKey);
}

export function studiesOfPatient(items: EcgStudyListItem[], patient: EcgPatient | null): EcgStudyListItem[] {
    if (!patient) return [];
    const key = patientKey(patient);
    return items.filter((item) => studyPatientKey(item) === key);
}

export function summarizePatient(
    items: EcgStudyListItem[],
    patient: EcgPatient | null,
): EcgPatientSummary | null {
    if (!patient) return null;
    const studies = studiesOfPatient(items, patient).sort((a, b) => compareStudies(b, a));
    let withConclusion = 0;
    const statusCounts: Record<string, number> = {};
    for (const study of studies) {
        if (study.conclusion.trim()) withConclusion++;
        const code = study.statusCode || 'unknown';
        statusCounts[code] = (statusCounts[code] || 0) + 1;
    }
    return {
        patient,
        studies,
        total: studies.length,
        withConclusion,
        awaitingConclusion: studies.length - withConclusion,
        lastAt: studies[0]?.createdAt || '',
        statusCounts,
    };
}
